import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as FileSaver from 'file-saver';
import { ValueService } from '../../../shared/value.service';
import { Utils } from '../../../shared/utils';
import { consts } from '../../../consts';
import { Option } from '../../../shared/models/option.model';
import { ConfirmService } from '../../../modules_v2/shared/services/confirm.service';
import { NotificationService } from '../../../modules_v2/shared/services/notification.service';
import { LoadingDialogService } from '../../../shared/ui/loading/loading-dialog.service';
import { ModelTaxService } from '../model-tax.service';
import { Model193Service } from '../model-193.service';
import { TaxFormHistoryDialogComponent, TaxFormHistoryResult } from '../tax-form-history/tax-form-history-dialog.component';
import { TaxFieldOriginDialogComponent } from '../tax-field-origin/tax-field-origin-dialog.component';
import { TaxFormDeclarationModel, TAX_FORM_DECLARATION_STATUS } from '../tax-form-declaration.model';
import { Model193, Model193Validation, normalizarModelo193, nuevoModelo193 } from './taxes-193.model';

const PERIODO_ANUAL = '0A';

@Component({
  selector: 'convfact-model193',
  templateUrl: './model-193.component.html',
  styleUrls: ['./model-193.component.css'],
  standalone: false
})
export class Model193Component implements OnInit {
  KEY_PATH = 'PATH_URL_MODEL_193';

  path: string;
  isLoading = true;
  isSaving = false;

  data: Model193 = nuevoModelo193();
  tipoDeclaracion = 'N';
  validations: Model193Validation[] = [];
  declaracionActual: TaxFormDeclarationModel;

  ejercicioSel: string;
  seccionActiva = 'declarante';
  regiones: { [region: string]: boolean } = {};
  confirmadas: { [region: string]: boolean } = {};
  buscadorCampo = '';
  mostrarGuia = false;
  version = 0;
  foco: { texto: string, id: number } | null = null;

  ejercicioOptions: Option[] = [];

  readonly sinoOptions: Option[] = [{ label: 'No', value: 'N' }, { label: 'Sí', value: 'S' }];

  readonly tipoDeclaracionOptions: Option[] = [
    { label: 'Normal', value: 'N' },
    { label: 'Complementaria', value: 'C' },
    { label: 'Sustitutiva', value: 'S' }
  ];

  readonly secciones: { clave: string, titulo: string, confirmacion: string }[] = [
    { clave: 'declarante', titulo: 'Declarante', confirmacion: 'Declarante confirmado' },
    { clave: 'declaracion', titulo: 'Declaración', confirmacion: 'Declaración confirmada' },
    { clave: 'resumen', titulo: 'Resumen de totales', confirmacion: 'Resumen confirmado' },
    { clave: 'perceptores', titulo: 'Perceptores', confirmacion: 'Perceptores confirmados' },
    { clave: 'presentacion', titulo: 'Presentación', confirmacion: 'Presentación confirmada' }
  ];

  private readonly camposBuscables: { id: string, texto: string, region: string }[] = [
    { id: 'sujetoNIF', texto: 'nif declarante', region: 'declarante' },
    { id: 'sujetoRazonSocial', texto: 'razon social denominacion apellidos nombre declarante', region: 'declarante' },
    { id: 'ejercicio', texto: 'ejercicio anio', region: 'declarante' },
    { id: 'contactoTelefono', texto: 'telefono contacto', region: 'declarante' },
    { id: 'contactoNombre', texto: 'persona contacto relacionarse nombre', region: 'declarante' },
    { id: 'complementaria', texto: 'tipo declaracion normal complementaria sustitutiva', region: 'declaracion' },
    { id: 'justificante', texto: 'justificante declaracion anterior', region: 'declaracion' },
    { id: 'numeroIdentificativo', texto: 'numero identificativo', region: 'declaracion' },
    { id: 'naturalezaDeclarante', texto: 'naturaleza declarante', region: 'declaracion' },
    { id: 'numeroPerceptores', texto: 'numero total perceptores', region: 'resumen' },
    { id: 'totalBase', texto: 'base retenciones ingresos cuenta', region: 'resumen' },
    { id: 'retencionesIngresadas', texto: 'retenciones ingresos cuenta ingresados', region: 'resumen' },
    { id: 'totalRetenciones', texto: 'total retenciones ingresos cuenta', region: 'resumen' },
    { id: 'perceptores', texto: 'perceptores', region: 'perceptores' }
  ];

  private readonly KEY_REGIONES = 'cf-193-regiones';
  private readonly KEY_CONFIRMADAS = 'cf-193-confirmadas';

  private justificanteConsultado = '';
  private focoContador = 0;

  constructor(
    private service: Model193Service,
    private draftService: ModelTaxService<any>,
    private valueService: ValueService,
    private dialog: MatDialog,
    private confirmService: ConfirmService,
    private notification: NotificationService,
    private loading: LoadingDialogService
  ) { }

  ngOnInit(): void {
    this.getPath();
    this.cargarRegiones();

    const cerrado = new Date().getFullYear() - 1;

    this.ejercicioOptions = Utils.GetListYears()
      .filter((anio: number) => anio <= new Date().getFullYear())
      .map((anio: number) => ({ label: anio.toString(), value: anio.toString() }));

    this.ejercicioSel = cerrado.toString();
    this.cargarEjercicio(true);
  }

  get ejercicio(): number {
    return Number(this.ejercicioSel) || this.data.ejercicio;
  }

  get naturalezaDeclaranteValor(): string {
    return this.data.naturalezaDeclarante ? 'S' : 'N';
  }

  get pideJustificante(): boolean {
    return this.tipoDeclaracion !== 'N';
  }

  get errores(): Model193Validation[] {
    return this.validations.filter(item => item.type !== 'WARNING');
  }

  get avisosValidacion(): Model193Validation[] {
    return this.validations.filter(item => item.type === 'WARNING');
  }

  get estadoDeclaracion(): string {
    if (!this.declaracionActual) return 'Sin guardar';
    if (this.declaracionActual.status === TAX_FORM_DECLARATION_STATUS.presented) return 'Presentada';
    if (this.declaracionActual.status === TAX_FORM_DECLARATION_STATUS.downloaded) return 'Fichero descargado';

    return 'Borrador guardado';
  }

  get varianteDeclaracion(): string {
    if (!this.declaracionActual) return 'neutral';
    if (this.declaracionActual.status === TAX_FORM_DECLARATION_STATUS.presented) return 'success';
    if (this.declaracionActual.status === TAX_FORM_DECLARATION_STATUS.downloaded) return 'info';

    return 'warning';
  }

  get nombreFichero(): string {
    const nif = (this.data.sujetoNIF || '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();

    return [nif, this.ejercicioSel].filter(parte => !!parte).join('_') + '.193';
  }

  trackByIndex(i: number) { return i; }

  mensaje(clave: string): string {
    const item = this.validations.find(v => v.key === clave);

    return item ? item.message : '';
  }

  esPerceptor(item: Model193Validation): boolean {
    return (item.key || '').startsWith('perceptor_');
  }

  private payload(): Model193 {
    const modelo = { ...this.data, modelo: '193', ejercicio: this.ejercicio };

    modelo.complementaria = this.tipoDeclaracion === 'C';
    modelo.sustitutiva = this.tipoDeclaracion === 'S';

    if (this.tipoDeclaracion === 'N') {
      modelo.justificante = '';
    }

    return modelo;
  }

  private esBorradorVigente(borrador: any): boolean {
    return !!borrador && borrador.modelo === '193' && typeof borrador.numeroPerceptores === 'number'
      && borrador.sujetoNIF !== undefined;
  }

  cambiarEjercicio() {
    this.cargarEjercicio(false);
  }

  private cargarEjercicio(primeraCarga: boolean) {
    const ejercicio = this.ejercicioSel;

    if (!ejercicio) return;

    this.isLoading = primeraCarga;

    const tarea = (async () => {
      const borradores = await this.draftService.getDrafts('193', ejercicio).catch(() => []);
      const existente = (borradores || [])
        .find(item => item.period === PERIODO_ANUAL && item.taxYear.toString() === ejercicio);

      if (existente) {
        const borrador = await this.draftService.getDraft('193', existente.uid).catch(() => null);

        if (this.esBorradorVigente(borrador)) {
          return await this.service.recalculate(borrador as Model193).catch(() => borrador as Model193);
        }
      }

      return await this.service.calculate(ejercicio);
    })();

    const ejecucion = primeraCarga ? tarea : this.loading.run('Cargando el ejercicio...', tarea);

    ejecucion.then(modelo => {
      this.isLoading = false;
      this.aplicarModelo(modelo);
      this.cargarDeclaracionActual();
    }).catch(() => {
      this.isLoading = false;
      this.notification.error('Ha ocurrido un error inesperado al obtener la información');
    });
  }

  private aplicarModelo(modelo: Model193, avisar = true) {
    this.data = normalizarModelo193(modelo);
    this.data.ejercicio = this.ejercicio;
    this.tipoDeclaracion = this.data.sustitutiva ? 'S' : (this.data.complementaria ? 'C' : 'N');
    this.justificanteConsultado = this.tipoDeclaracion === 'N' ? '' : (this.data.justificante || '').trim();
    this.validations = [];
    this.version++;

    if (avisar && this.data.avisos.length) {
      this.notification.warning(this.data.avisos[0]);
    }
  }

  private cargarDeclaracionActual() {
    const ejercicio = this.ejercicioSel;

    this.draftService.getDrafts('193', ejercicio)
      .then(response => {
        this.declaracionActual = (response || [])
          .find(item => item.period === PERIODO_ANUAL && item.taxYear.toString() === ejercicio);
      })
      .catch(() => { });
  }

  onNaturalezaDeclarante(valor: string) {
    this.data.naturalezaDeclarante = valor === 'S';
    this.refrescarTotales();
  }

  onTipoDeclaracion(valor: string) {
    this.tipoDeclaracion = valor || 'N';

    if (this.tipoDeclaracion === 'N') {
      this.data.justificante = '';
      this.justificanteConsultado = '';
    }

    this.refrescarTotales();
  }

  onJustificante(valor: string) {
    this.data.justificante = valor;

    if ((valor || '').trim().length === 13 && valor !== this.justificanteConsultado) {
      this.justificanteConsultado = valor;
      this.refrescarTotales();
    }
  }

  refrescarTotales() {
    this.service.recalculate(this.payload())
      .then(modelo => {
        this.data.numeroIdentificativo = modelo.numeroIdentificativo || this.data.numeroIdentificativo;
        this.data.justificante = modelo.justificante;
        this.data.numeroPerceptores = modelo.numeroPerceptores;
        this.data.totalBase = modelo.totalBase;
        this.data.totalRetenciones = modelo.totalRetenciones;
        this.data.retencionesIngresadas = modelo.retencionesIngresadas;
        this.data.gastos = modelo.gastos;
        this.data.avisos = modelo.avisos;
      })
      .catch(() => this.notification.warning('No se han podido recalcular los totales'));
  }

  onPerceptoresCambiados() {
    this.refrescarTotales();

    if (this.validations.length) {
      this.validate(true);
    }
  }

  calcular() {
    const contacto = { telefono: this.data.contactoTelefono, nombre: this.data.contactoNombre };
    const tipo = this.tipoDeclaracion === 'N' ? undefined : this.tipoDeclaracion;
    const justificante = tipo ? (this.data.justificante || '').trim() : '';

    this.loading.run('Calculando el modelo...', this.service.calculate(this.ejercicioSel, tipo, justificante))
      .then(modelo => {
        this.aplicarModelo(modelo);

        if (!this.data.contactoTelefono) this.data.contactoTelefono = contacto.telefono;
        if (contacto.nombre) this.data.contactoNombre = contacto.nombre;
      })
      .catch(err => this.notification.error(this.mensajeError(err, 'Ha ocurrido un error inesperado al calcular el modelo')));
  }

  validate(silencioso = false) {
    const tarea = this.service.validate(this.payload());
    const ejecucion = silencioso ? tarea : this.loading.run('Validando la declaración...', tarea);

    ejecucion
      .then(lista => {
        this.validations = lista;

        if (silencioso) return;

        const errores = this.errores;

        if (!lista.length) {
          this.notification.ok('La declaración no tiene errores');
          return;
        }

        if (!errores.length) {
          this.notification.warning('Se puede presentar, pero hay avisos que revisar');
          return;
        }

        this.notification.warning(errores.length === 1 ? 'Hay 1 error que corregir' : `Hay ${errores.length} errores que corregir`);
        this.irAlCampo(errores[0]);
      })
      .catch(() => {
        if (!silencioso) this.notification.error('Ha ocurrido un error inesperado al validar');
      });
  }

  irAlCampo(item: Model193Validation) {
    if (this.esPerceptor(item)) {
      const quien = (item.message || '').split(':')[0].trim();
      const nif = quien.split(' ')[0];

      this.foco = { texto: nif, id: ++this.focoContador };
      this.irALaSeccion('perceptores');
      return;
    }

    if (!item.key) return;

    this.irAlElemento(item.key, this.regionDelCampoPorId(item.key));
  }

  private regionDelCampoPorId(id: string): string {
    const campo = this.camposBuscables.find(item => item.id === id);

    return campo ? campo.region : 'declarante';
  }

  private irAlElemento(id: string, region: string) {
    this.abrirRegion(region);

    setTimeout(() => {
      const element = document.getElementById(id);

      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 60);
  }

  irAlCampoBuscado() {
    const buscado = this.normalizarBusqueda(this.buscadorCampo);

    if (!buscado) return;

    const campo = this.camposBuscables.find(item => item.id.toLowerCase() === buscado)
      || this.camposBuscables.find(item => item.texto.includes(buscado));

    if (!campo) {
      this.notification.warning(`No hay ningún campo "${this.buscadorCampo}" en el modelo 193`);
      return;
    }

    this.irAlElemento(campo.id, campo.region);
  }

  private normalizarBusqueda(valor: string): string {
    return (valor || '').trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  }

  colapsada(region: string): boolean {
    return !!this.regiones[region];
  }

  confirmada(region: string): boolean {
    return !!this.confirmadas[region];
  }

  toggleRegion(region: string) {
    this.regiones[region] = !this.regiones[region];

    if (!this.regiones[region]) {
      this.seccionActiva = region;
    }

    this.guardarRegiones();
  }

  get todoCompactado(): boolean {
    return this.secciones.every(seccion => this.regiones[seccion.clave]);
  }

  alternarTodo() {
    const compactar = !this.todoCompactado;

    this.secciones.forEach(seccion => this.regiones[seccion.clave] = compactar);
    this.guardarRegiones();
  }

  private abrirRegion(region: string) {
    this.seccionActiva = region;

    if (this.regiones[region]) {
      this.regiones[region] = false;
      this.guardarRegiones();
    }
  }

  irALaSeccion(region: string) {
    this.abrirRegion(region);
    this.bajarALaSeccion(region);
  }

  confirmarRegion(region: string) {
    this.confirmadas[region] = true;
    this.regiones[region] = true;

    const posicion = this.secciones.findIndex(item => item.clave === region);
    const siguiente = this.secciones
      .slice(posicion + 1)
      .find(candidata => !this.confirmadas[candidata.clave]);

    if (siguiente) {
      this.regiones[siguiente.clave] = false;
    }

    const destino = siguiente ? siguiente.clave : region;

    this.seccionActiva = destino;
    this.guardarRegiones();
    this.bajarALaSeccion(destino);
  }

  private bajarALaSeccion(region: string) {
    setTimeout(() => {
      const element = document.getElementById(`region-${region}`);

      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  private cargarRegiones() {
    try {
      const regiones = localStorage.getItem(this.KEY_REGIONES);
      const confirmadas = localStorage.getItem(this.KEY_CONFIRMADAS);

      this.confirmadas = confirmadas ? JSON.parse(confirmadas) : {};

      if (regiones) {
        this.regiones = JSON.parse(regiones);
        return;
      }
    } catch {
      this.confirmadas = {};
    }

    this.recorridoInicial();
  }

  private recorridoInicial() {
    this.regiones = {};
    this.confirmadas = {};

    this.secciones.forEach((seccion, indice) => this.regiones[seccion.clave] = indice > 0);
    this.seccionActiva = this.secciones[0].clave;

    this.guardarRegiones();
  }

  private guardarRegiones() {
    try {
      localStorage.setItem(this.KEY_REGIONES, JSON.stringify(this.regiones));
      localStorage.setItem(this.KEY_CONFIRMADAS, JSON.stringify(this.confirmadas));
    } catch { }
  }

  getFile() {
    if (!Utils.IsTierFour()) {
      this.notification.warning(consts.UPDATE_SUBSCRIPTION_TIER_MESSAGE);
      return;
    }

    this.loading.run('Generando el fichero...', this.service.getFile(this.payload()))
      .then(resultado => {
        this.validations = [];
        FileSaver.saveAs(resultado.blob, resultado.nombre || this.nombreFichero);
        this.mostrarGuia = true;
        this.notification.ok('Fichero descargado');
        this.cargarDeclaracionActual();
      })
      .catch(async err => {
        const cuerpo = await this.leerError(err);

        if (cuerpo) {
          this.mostrarFaltantes(cuerpo);
          return;
        }

        this.notification.error('Ha ocurrido un error inesperado al generar el fichero');
      });
  }

  private async leerError(err: any): Promise<any> {
    try {
      if (err?.error instanceof Blob) {
        return JSON.parse(await err.error.text());
      }

      return err?.error ?? null;
    } catch {
      return null;
    }
  }

  private mostrarFaltantes(respuesta: any) {
    this.validations = Array.isArray(respuesta?.value) ? respuesta.value : [];
    const faltan = this.errores.length;

    if (!faltan) {
      this.notification.error(respuesta?.error?.messages?.[0] || 'No se ha podido generar el fichero');
      return;
    }

    this.notification.warning(faltan === 1
      ? 'Hay 1 error que impide generar el fichero'
      : `Hay ${faltan} errores que impiden generar el fichero`);

    this.irAlCampo(this.errores[0]);
  }

  saveDraft() {
    this.isSaving = true;

    const modelo = this.payload();

    this.loading.run('Guardando el borrador...', this.draftService.saveDraft(
      { ...modelo, periodo: PERIODO_ANUAL },
      {
        taxYear: this.ejercicio,
        amount: modelo.totalRetenciones,
        declarationType: this.tipoDeclaracion === 'N' ? null : this.tipoDeclaracion,
        receiptNumber: modelo.justificante
      }))
      .then(() => {
        this.isSaving = false;
        this.notification.ok('Borrador guardado');
        this.cargarDeclaracionActual();
      })
      .catch(() => {
        this.isSaving = false;
        this.notification.error('No se ha podido guardar el borrador');
      });
  }

  openHistory() {
    this.dialog.open(TaxFormHistoryDialogComponent, {
      width: '960px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data: { modelo: '193', titulo: 'modelo 193' }
    }).afterClosed().subscribe((result: TaxFormHistoryResult) => {
      if (result?.action === 'open') {
        this.abrirDeclaracion(result.declaration);
      }
    });
  }

  private abrirDeclaracion(declaration: TaxFormDeclarationModel) {
    this.loading.run('Abriendo la declaración...', this.draftService.getDraft('193', declaration.uid))
      .then(async (borrador: any) => {
        this.ejercicioSel = declaration.taxYear.toString();

        if (this.esBorradorVigente(borrador)) {
          this.aplicarModelo(await this.service.recalculate(borrador).catch(() => borrador));
        } else {
          this.notification.warning('Esta declaración se guardó con el formato anterior: se muestran los datos calculados');
          this.aplicarModelo(await this.service.calculate(this.ejercicioSel));
        }

        this.cargarDeclaracionActual();
      })
      .catch(() => this.notification.error('No se ha podido abrir la declaración'));
  }

  marcarPresentada() {
    if (!this.declaracionActual) {
      this.notification.warning('Guarda el borrador antes de marcarlo como presentado');
      return;
    }

    this.confirmService.open({
      title: 'Marcar como presentada',
      subtitle: `Ejercicio ${this.ejercicioSel}`,
      message: 'Se guardará como presentada en el historial. Esto no presenta la declaración: la presentación se hace en la sede de la Agencia Tributaria.',
      icon: 'check_circle',
      tone: 'info',
      confirmText: 'Marcar como presentada',
      action: () => this.draftService.setDraftStatus('193', this.declaracionActual.uid,
        TAX_FORM_DECLARATION_STATUS.presented)
    }).subscribe(confirmed => {
      if (confirmed) {
        this.notification.ok('Declaración marcada como presentada');
        this.cargarDeclaracionActual();
      }
    });
  }

  descartarBorrador() {
    if (!this.declaracionActual) return;

    this.confirmService.open({
      title: 'Descartar borrador',
      subtitle: `Ejercicio ${this.ejercicioSel}`,
      message: 'Se borra lo guardado de este ejercicio y el formulario vuelve a los datos calculados a partir de tus facturas recibidas.',
      icon: 'restore',
      tone: 'danger',
      confirmText: 'Descartar y reiniciar',
      action: () => this.draftService.deleteDraft('193', this.declaracionActual.uid)
    }).subscribe(confirmed => {
      if (!confirmed) return;

      this.declaracionActual = null;
      this.recorridoInicial();
      this.notification.ok('Borrador descartado');
      this.tipoDeclaracion = 'N';
      this.calcular();
    });
  }

  importFile(event: any) {
    const file: File = event?.target?.files?.[0];

    if (!file) return;

    const reader = new FileReader();

    reader.onload = () => {
      this.loading.run('Importando el fichero...', this.service.importFile(reader.result as string))
        .then(modelo => {
          if (modelo.ejercicio && this.ejercicioOptions.some(item => item.value === modelo.ejercicio.toString())) {
            this.ejercicioSel = modelo.ejercicio.toString();
          }

          this.aplicarModelo(modelo, false);
          this.cargarDeclaracionActual();
          this.notification.ok('Fichero importado');
        })
        .catch(err => this.notification.error(this.mensajeError(err, 'El fichero no se ha podido importar')));
    };

    reader.onerror = () => this.notification.error('No se ha podido leer el fichero');
    reader.readAsText(file, 'ISO-8859-1');
    event.target.value = '';
  }

  openOrigen(campo: string) {
    this.loading.run('Buscando de dónde sale...', this.service.getOrigen(this.payload(), campo))
      .then(origen => {
        if (!origen) {
          this.notification.warning('Este importe no tiene desglose');
          return;
        }

        this.dialog.open(TaxFieldOriginDialogComponent, {
          width: '720px',
          maxWidth: '96vw',
          maxHeight: '90vh',
          autoFocus: false,
          data: { origen }
        });
      })
      .catch(() => this.notification.warning('Este importe no tiene desglose'));
  }

  private mensajeError(err: any, defecto: string): string {
    return err?.error?.error?.messages?.[0] || defecto;
  }

  redirectFileTax() {
    window.open(this.path, '_blank');
  }

  getPath() {
    this.valueService.getValue(this.KEY_PATH)
      .then(response => {
        this.path = response?.data?.data;
      })
      .catch(() => { });
  }
}
